import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { MdKeyboardArrowDown } from "react-icons/md";
import { motion, useAnimation, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";

const FAQ = () => {
  const [activeAnswer, setActiveAnswer] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const { t } = useTranslation();

  const animation1 = useAnimation();

  useEffect(() => {
    if (isInView) {
      animation1.start("visible");
    }
    // eslint-disable-next-line
  }, [isInView]);

  return (
    <Container>
      <div className="title">
        <motion.h1
          variants={{
            hidden: { opacity: 0, y: 100 },
            visible: { opacity: 1, y: 0 },
          }}
          initial="hidden"
          animate={animation1}
          transition={{ duration: 0.4, delay: 0.2, type: "spring" }}
        >
          {t("faqTitle")} <span>{t("faqTitleHighlight")}</span>
        </motion.h1>
        <motion.hr
          variants={{
            hidden: { opacity: 0, width: 0 },
            visible: { opacity: 1, width: "50%" },
          }}
          initial="hidden"
          animate={animation1}
          transition={{ duration: 1, delay: 0.3, type: "spring" }}
        />
      </div>
      <div ref={ref} className="questions">
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
          }}
          initial="hidden"
          animate={animation1}
          transition={{ duration: 0.4, delay: 0.3, type: "spring" }}
          className={activeAnswer === 1 ? "active" : "row"}
          onClick={() => setActiveAnswer(activeAnswer === 1 ? null : 1)}
        >
          <span>
            <h2>{t("faqQuestion1")}</h2>
            <MdKeyboardArrowDown className="icon" />
          </span>
          <p>
            {t("faqAnswer1")}
          </p>
        </motion.div>

        <hr />
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
          }}
          initial="hidden"
          animate={animation1}
          transition={{ duration: 0.4, delay: 0.5, type: "spring" }}
          className={activeAnswer === 3 ? "active" : "row"}
          onClick={() => setActiveAnswer(activeAnswer === 3 ? null : 3)}
        >
          <span>
            <h2>{t("faqQuestion2")}</h2>
            <MdKeyboardArrowDown className="icon" />
          </span>
          <p>{t("faqAnswer2")}</p>
        </motion.div>
      </div>
    </Container>
  );
};
const Container = styled.div`
  padding: 7em 4%;
  .title {
    padding: 0 10%;
    h1 {
      font-family: "Comfortaa";
      font-size: 2.4em;
      font-weight: 100;
      color: #b4b4b4;
      span {
        color: var(--primaryColor);
      }
    }
    hr {
      width: 50%;
      margin-top: 1em;
    }
  }
  .questions {
    transition: 0.4s;
    display: flex;
    flex-direction: column;
    gap: 1em;
    margin: 2em 0;
    align-items: center;
    hr {
      width: 80%;
      border: none;
      border-top: 1px solid #cacaca;
    }
    .row {
      transition: 0.4s;
      height: 30px;
      width: 80%;
      cursor: pointer;
      span {
        display: flex;
        justify-content: space-between;
        align-items: center;
        h2 {
          color: #d6d6d6;
          font-weight: 400;
          font-size: 18px;
        }
        .icon {
          font-size: 2em;
          color: var(--primaryColor);
        }
      }
      p {
        display: none;
      }
    }

    .active {
      transition: 0.4s;
      height: 50px;
      width: 80%;
      cursor: pointer;
      p {
        transition: 0.4s;
        display: flex;
        color: #a0a0a0;
        font-weight: 300;
      }
      span {
        display: flex;
        justify-content: space-between;
        align-items: center;
        h2 {
          font-weight: 500;
          font-size: 20px;
          transition: 0.4s;
        }
        .icon {
          transform: rotate(180deg);
          font-size: 2em;
          color: var(--primaryColor);
        }
      }
    }
  }
  @media (max-width: 1170px) {
    .questions {
      hr {
        width: 97%;
      }
      .row {
        width: 98%;
      }
      .active {
        width: 98%;
      }
    }
  }
  @media (max-width: 955px) {
    .questions {
      .row {
        h2 {
          font-size: 1.4em;
        }
      }
      .active {
        width: 98%;
        height: 70px;
        h2 {
          font-size: 1.4em;
        }
        p {
          line-height: 20px;
        }
      }
    }
  }
  @media (max-width: 870px) {
    background-size: 40% 100%, 100% 140%, 79% 100%;
    .questions {
      .active {
        height: 87px;
      }
    }
  }
  @media (max-width: 730px) {
    .questions {
      .active {
        height: 110px;
      }
    }
  }
  @media (max-width: 680px) {
    .questions {
      .row {
        height: 40px;
        h2 {
          line-height: 24px;
        }
      }
    }
  }
  @media (max-width: 540px) {
    .questions {
      .active {
        height: 110px;
        p {
          line-height: 18px;
        }
      }
    }
  }
  @media (max-width: 425px) {
    .questions {
      .row {
        h2 {
          font-size: 1.2em;
        }
      }
      .active {
        h2 {
          font-size: 1.2em;
        }
        p {
          font-size: 15px;
        }
      }
    }
  }
  @media (max-width: 365px) {
    .questions {
      .row {
        h2 {
          height: 180px;
          font-size: 1.2em;
        }
      }
      .active {
        height: 160px;
        h2 {
          font-size: 1.2em;
        }
        p {
          font-size: 15px;
        }
      }
    }
  }
`;

export default FAQ;
